import { ReactElement } from 'react'
import styled from 'styled-components'
import { ISeller } from '../../../../../../../../src/services/MultiSellerServicesProvider'
import { defaultTheme } from '../../../../../../../../src/theme'
import { I18n, MY_PLACE_SELLER } from 'common'
import { Order } from '../../../../../../../../src/api/cart/interfaces'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'
import { Thumbnail } from '../../../../../../../../src/ui-components/products/Thumbnail'
import {
  ReceiptDivider,
  ReceiptLabel,
  ReceiptTextRow,
  ReceiptValue,
} from '../../printStyles'

interface Props {
  order: Order
  coverageKey: string
  poNumber: string
  noteToStore: string
  isMyPlaceOrder: boolean
  sellersData: ISeller[]
}

const Header = (props: Props): ReactElement => {
  const {
    order,
    sellersData,
    coverageKey,
    poNumber,
    noteToStore,
    isMyPlaceOrder,
  } = props
  const { isDisplayMultiSellerLogos } = useUiStateStore()

  const getSeller = (key: number | string | undefined): ISeller => {
    const seller = sellersData?.find((s) => s.coverageKey === key)
    return seller ?? MY_PLACE_SELLER
  }

  function getSellerLogoUrl(key: number | string | undefined): string {
    const seller = getSeller(key)
    if (seller) {
      return seller.logoUrl
    }
    if (key === '') {
      return MY_PLACE_SELLER.logoUrl
    }
    return ''
  }

  function getSellerName(key: number | string | undefined): string {
    const seller = getSeller(key)
    if (seller) {
      return seller.sellerName
    }
    if (key === '') {
      return `${MY_PLACE_SELLER.sellerName} Order`
    }
    return ''
  }

  const shipToAddress = order?.deliveryDetail?.address
    ? order.deliveryDetail.address.city
      ? `${order.deliveryDetail.address.line1}, ${order.deliveryDetail.address.city}, ${order.deliveryDetail.address.region} ${order.deliveryDetail.address.postalCode}`
      : order.deliveryDetail.address.line1
    : ''

  return (
    <Wrapper>
      <TitleWrapper>
        {(isDisplayMultiSellerLogos() || !coverageKey) && (
          <Thumbnail
            urlOrBase64={getSellerLogoUrl(coverageKey)}
            alt="Seller"
            width={96}
            height={24}
            placeholder
          />
        )}
        <SellerName>{getSellerName(coverageKey)}</SellerName>
      </TitleWrapper>
      <ReceiptTextRow>
        <ReceiptLabel>
          <I18n name="orderNumber" />
        </ReceiptLabel>
        <ReceiptValue>{order.orderNumber}</ReceiptValue>
        <ReceiptDivider>|</ReceiptDivider>
        <ReceiptLabel>
          <I18n name="confirmationID" />
        </ReceiptLabel>
        <ReceiptValue>{order?.parts[0]?.orderConfirmationNbr}</ReceiptValue>
        <ReceiptDivider>|</ReceiptDivider>
        <ReceiptLabel>
          <I18n name="poNumber" />
        </ReceiptLabel>
        <ReceiptValue>{poNumber}</ReceiptValue>
        {noteToStore && (
          <>
            <ReceiptDivider>|</ReceiptDivider>
            <ReceiptLabel>
              <I18n name="noteToStore" />
            </ReceiptLabel>
            <ReceiptValue>{noteToStore}</ReceiptValue>
          </>
        )}
        {isMyPlaceOrder && shipToAddress && (
          <>
            <ReceiptDivider>|</ReceiptDivider>
            <ReceiptLabel>
              <I18n name="shipTo" />
            </ReceiptLabel>
            <ReceiptValue>{shipToAddress}</ReceiptValue>
          </>
        )}
      </ReceiptTextRow>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  background-color: ${defaultTheme.colors.rowFeaturedBackground};
  border-top: 1px solid ${defaultTheme.colors.disabledBackground};
  border-bottom: 1px solid ${defaultTheme.colors.disabledBackground};
  padding: 2px 4px;
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const SellerName = styled.div`
  font-family: Oswald, serif;
  font-weight: 700;
  font-size: 11pt;
  line-height: 14pt;
`

export default Header
